import {
    IMerchantFilteringExchangeLocation,
    ICountryZone,
    REST_OF_WORLD,
} from '../../../../../models/User';
import {IPlatformLocation} from '../../../../return-methods/return-method-types';

/** Zone display type for UI rendering */
export enum ZONE_TYPE {
    PREDEFINED_GROUP = 'PREDEFINED_GROUP',
    COUNTRY = 'COUNTRY',

    /** Currently not in use. Left here for further implementation */
    PROVINCE = 'PROVINCE',
    CITY = 'CITY',
}

/** Zone display item with label, code, and type */
export interface IZoneDisplayItem {
    label: string;
    code: string;
    type: ZONE_TYPE;
}

/** Table row interface (defined here for use by the builder) */
export interface IExchangeLocationRuleRow {
    warehouse_labels: string[];
    zone_items: IZoneDisplayItem[];
    is_fallback: boolean;
    locations: IMerchantFilteringExchangeLocation[];
}

/** Stateless utility for building table rows from location data */
class ExchangeLocationsTableBuilder {
    
    private readonly _warehouseMap: Map<string, IPlatformLocation>;
    private readonly _countryMap: Map<string, ICountryZone>;

    constructor(
        private readonly _warehouses: IPlatformLocation[],
        private readonly _countries: ICountryZone[]
    ) {
        this._warehouseMap = this._buildWarehouseMap();
        this._countryMap = this._buildCountryMap();
    }

    /** Main method: build table rows from locations array */
    public buildTableRows(locations: IMerchantFilteringExchangeLocation[]): IExchangeLocationRuleRow[] {
        
        /** Separate fallback (REST_OF_WORLD) from regular rules */
        const fallbackLocations: IMerchantFilteringExchangeLocation[] = [];
        const regularLocations: IMerchantFilteringExchangeLocation[] = [];

        locations.forEach((location) => {
            if (location.predefined_country_group === REST_OF_WORLD) {
                fallbackLocations.push(location);
            }
            else {
                regularLocations.push(location);
            }
        });

        /** Build regular rows */
        const rows = this._groupLocationsByWarehouses(regularLocations).map(
            (groupLocations) => this._buildRow(groupLocations, false)
        );

        /** Sort rows by first warehouse label to maintain stable order */
        rows.sort((rowA, rowB) => {
            const labelA = rowA.warehouse_labels[0] || '';
            const labelB = rowB.warehouse_labels[0] || '';
            return labelA.localeCompare(labelB);
        });

        /**
         * Fallback row logic (compressed mode):
         * - Empty fallbackLocations = all warehouses selected (compressed state)
         * - Non-empty fallbackLocations = specific warehouses selected
         */
        if (fallbackLocations.length === 0) {
            rows.push(this._buildCompressedFallbackRow());
        }
        else {
            rows.push(this._buildRow(fallbackLocations, true));
        }

        return rows;
    }

    /** Group locations by unique warehouse set (warehouses with identical zone sets go into one group) */
    private _groupLocationsByWarehouses(locations: IMerchantFilteringExchangeLocation[]): IMerchantFilteringExchangeLocation[][] {
        
        /**
         * Step 1: Group locations by warehouse_id
         * Result: Map {
         *   "warehouse_1" => [
         *     { location_id: "warehouse_1", country_code: "US" },
         *     { location_id: "warehouse_1", country_code: "CA" }
         *   ],
         *   "warehouse_2" => [
         *     { location_id: "warehouse_2", country_code: "US" },
         *     { location_id: "warehouse_2", country_code: "CA" }
         *   ],
         *   "warehouse_3" => [
         *     { location_id: "warehouse_3", country_code: "GB" }
         *   ]
         * }
         */
        const locationsByWarehouse = this._groupByWarehouse(locations);

        /**
         * Step 2: Calculate zone signature for each warehouse (JSON.stringify of sorted zone codes)
         * Result: Map {
         *   "warehouse_1" => '["CA","US"]',
         *   "warehouse_2" => '["CA","US"]',
         *   "warehouse_3" => '["GB"]'
         * }
         */
        const warehouseSignatures = this._buildWarehouseSignatures(locationsByWarehouse);

        /**
         * Step 3: Group locations by identical zone signatures
         * Result: Map {
         *   '["CA","US"]' => [
         *     { location_id: "warehouse_1", country_code: "US" },
         *     { location_id: "warehouse_1", country_code: "CA" },
         *     { location_id: "warehouse_2", country_code: "US" },
         *     { location_id: "warehouse_2", country_code: "CA" }
         *   ],
         *   '["GB"]' => [
         *     { location_id: "warehouse_3", country_code: "GB" }
         *   ]
         * }
         */
        const locationsBySignature = this._groupBySignature(locationsByWarehouse, warehouseSignatures);

        /**
         * Step 4: Return as an array of location groups
         * Result: [
         *   [{ warehouse_1, US }, { warehouse_1, CA }, { warehouse_2, US }, { warehouse_2, CA }],
         *   [{ warehouse_3, GB }]
         * ]
         */
        return Array.from(locationsBySignature.values());
    }

    /** Group locations by warehouse_id */
    private _groupByWarehouse(
        locations: IMerchantFilteringExchangeLocation[]
    ): Map<string, IMerchantFilteringExchangeLocation[]> {
        const result = new Map<string, IMerchantFilteringExchangeLocation[]>();
        
        locations.forEach((location) => {
            const warehouseLocations = result.get(location.location_id) || [];
            warehouseLocations.push(location);
            result.set(location.location_id, warehouseLocations);
        });
        
        return result;
    }

    /** Build zone signature for each warehouse */
    private _buildWarehouseSignatures(
        locationsByWarehouse: Map<string, IMerchantFilteringExchangeLocation[]>
    ): Map<string, string> {
        const signatures = new Map<string, string>();
        
        locationsByWarehouse.forEach((locationByWarehouse, warehouseId) => {

            const zoneCodes = locationByWarehouse
                .map((location) => this._getZoneCode(location))
                .sort();
            
            const zoneSignature = JSON.stringify(zoneCodes);
            
            signatures.set(warehouseId, zoneSignature);
        });
        
        return signatures;
    }

    /** Group locations by identical zone signatures */
    private _groupBySignature(
        locationsByWarehouse: Map<string, IMerchantFilteringExchangeLocation[]>,
        warehouseSignatures: Map<string, string>
    ): Map<string, IMerchantFilteringExchangeLocation[]> {

        const locationsByZoneSignature = new Map<string, IMerchantFilteringExchangeLocation[]>();
        
        locationsByWarehouse.forEach((warehouseLocations, warehouseId) => {
            const zoneSignature = warehouseSignatures.get(warehouseId)!;
            const groupLocations = locationsByZoneSignature.get(zoneSignature);
            
            if (groupLocations) {
                groupLocations.push(...warehouseLocations);
            }
            else {
                locationsByZoneSignature.set(zoneSignature, [...warehouseLocations]);
            }
        });
        
        return locationsByZoneSignature;
    }

    /** Build a single row from a locations array */
    private _buildRow(
        locations: IMerchantFilteringExchangeLocation[],
        isFallback: boolean
    ): IExchangeLocationRuleRow {
        
        /**
         * Get unique warehouse IDs and sort them by warehouse name for stable order
         * Example: ["warehouse_1", "warehouse_2"] from locations with duplicate warehouse_ids
         */
        const warehouseIds = Array.from(
            new Set(locations.map((exchangeLocation) => exchangeLocation.location_id))
        ).sort((idA, idB) => {
            const warehouseA = this._warehouseMap.get(idA);
            const warehouseB = this._warehouseMap.get(idB);
            const nameA = warehouseA?.name ?? idA;
            const nameB = warehouseB?.name ?? idB;
            return nameA.localeCompare(nameB);
        });

        const warehouseLabels = this._resolveWarehouseLabels(warehouseIds);
        const zoneItems = this._buildZoneItems(locations);

        return {
            warehouse_labels: warehouseLabels,
            zone_items: zoneItems,
            is_fallback: isFallback,
            locations: locations,
        };
    }

    /**
     * Builds compressed fallback row with all warehouses.
     * Compressed mode: empty locations array means all warehouses are selected.
     */
    private _buildCompressedFallbackRow(): IExchangeLocationRuleRow {
        const allWarehouseIds = this._warehouses
            .map((warehouse) => warehouse.location_id)
            .sort((idA, idB) => {
                const warehouseA = this._warehouseMap.get(idA);
                const warehouseB = this._warehouseMap.get(idB);
                const nameA = warehouseA?.name ?? idA;
                const nameB = warehouseB?.name ?? idB;
                return nameA.localeCompare(nameB);
            });

        const warehouseLabels = this._resolveWarehouseLabels(allWarehouseIds);

        const zoneItems: IZoneDisplayItem[] = [{
            label: 'All other zones',
            code: REST_OF_WORLD,
            type: ZONE_TYPE.PREDEFINED_GROUP,
        }];

        return {
            warehouse_labels: warehouseLabels,
            zone_items: zoneItems,
            is_fallback: true,
            locations: [],
        };
    }

    /** Resolve warehouse labels from IDs */
    private _resolveWarehouseLabels(locationIds: string[]): string[] {
        return locationIds.map((locationId) => {
            const warehouse = this._warehouseMap.get(locationId);
            return warehouse ? (warehouse.name ?? locationId) : locationId;
        });
    }

    /** Build zone display items from locations */
    private _buildZoneItems(locations: IMerchantFilteringExchangeLocation[]): IZoneDisplayItem[] {
        const zoneItems: IZoneDisplayItem[] = [];
        const uniqueCodes = new Set<string>();

        locations.forEach((location) => {
            const code = this._getZoneCode(location);

            /** Skip duplicates */
            if (uniqueCodes.has(code)) {
                return;
            }

            uniqueCodes.add(code);

            const label = this._resolveZoneLabel(location);
            const type = this._resolveZoneType(location);
            zoneItems.push({ label, code, type });
        });

        /** Sort zone items by label for stable order */
        zoneItems.sort((itemA, itemB) => itemA.label.localeCompare(itemB.label));

        return zoneItems;
    }

    /** Resolve human-readable zone label */
    private _resolveZoneLabel(location: IMerchantFilteringExchangeLocation): string {
        if (location.country_code) {
            const country = this._countryMap.get(location.country_code);
            return country ? country.label : location.country_code;
        }

        return 'Unknown';
    }

    /** Get zone code for sorting/comparison */
    private _getZoneCode(location: IMerchantFilteringExchangeLocation): string {
        return location.predefined_country_group || location.country_code || '';
    }

    /** Determine zone type */
    private _resolveZoneType(location: IMerchantFilteringExchangeLocation): ZONE_TYPE {
        return location.predefined_country_group === REST_OF_WORLD ? ZONE_TYPE.PREDEFINED_GROUP : ZONE_TYPE.COUNTRY;
    }

    /**
     * Build warehouse lookup map (O(1) access)
     * Example result:
     * Map {
     *   "warehouse_123" => { location_id: "warehouse_123", name: "New York Warehouse", ... },
     *   "warehouse_456" => { location_id: "warehouse_456", name: "London Warehouse", ... }
     * }
     */
    private _buildWarehouseMap(): Map<string, IPlatformLocation> {
        const warehouseMap = new Map<string, IPlatformLocation>();
        this._warehouses.forEach((warehouse) => {
            warehouseMap.set(warehouse.location_id, warehouse);
        });
        return warehouseMap;
    }

    /**
     * Build a country lookup map (O(1) access)
     * Example result:
     * Map {
     *   "US" => { label: "United States", code: "US" },
     *   "CA" => { label: "Canada", code: "CA" },
     *   "GB" => { label: "United Kingdom", code: "GB" }
     * }
     */
    private _buildCountryMap(): Map<string, ICountryZone> {
        const countryMap = new Map<string, ICountryZone>();
        this._countries.forEach((country) => {
            countryMap.set(country.code, country);
        });
        return countryMap;
    }
}

export default ExchangeLocationsTableBuilder;

